/**
 * Interstitial slide pool + selector (Step 17.2).
 *
 * Hand-curated slides that sit between question clusters in the /start
 * funnel. Triggered by quiz answers — the selector is a pure function
 * of `(slot, profile)` so SSR and client always pick the same slide.
 *
 * Hydration safety: zero browser-API reads, no Date.now, no random
 * tie-breaking. iconName is a string (the renderer maps it to a Lucide
 * component) so the config stays JSON-serializable across the
 * server/client boundary.
 *
 * Content discipline: education-slot copy (after_concerns / after_habits
 * / processing) is hedged ("often" / "may" / "common starting point") —
 * a writing convention, not a code-enforced check. What IS code-enforced
 * (scripts/test-interstitial-content.ts FORBIDDEN list, line ~130):
 * absence of diagnostic absolutes — "you have", "you will", "you are
 * diagnosed", "cures", "treats", "guarantee", "definitely". The absolutes
 * block applies to ALL slots (including future value/proof in Phase 1)
 * since medical-claim language is unsafe regardless of slot. Whether
 * real positive-hedge enforcement is wanted on education slots is a
 * future-phase call — Track 3.13-phase-0 surfaced the docblock-vs-code
 * mismatch but did not add the regex.
 */

import type {
  PrimaryConcern,
  ActivityLevel,
  DietPattern,
  DiagnosedCondition,
  PrimaryGoal,
  HealthProfileInput,
} from './types';
// Track 3.13-phase-0: SmokingStatus + AlcoholFrequency imports dropped
// alongside their trigger types. The lifestyle question no longer
// captures those signals (moved to clarity-AI per quiz-state.ts:21-22).

export type InterstitialSlot =
  | 'after_concerns'
  | 'after_habits'
  | 'processing';
// Track 3.13-phase-0: 'trust' removed — was dropped from buildFlow Phase 1
// (quiz-state.ts:21-22) and never reachable. Phase 1 will reintroduce slot
// types 'value' | 'proof' on the cleared slate.

export type InterstitialAccent = 'amber' | 'emerald' | 'navy';

/** Lucide icon name string (renderer maps to component). */
export type InterstitialIcon =
  | 'battery-charging'
  | 'moon'
  | 'sparkles'
  | 'heart-pulse'
  | 'heart'
  | 'clock'
  | 'activity'
  | 'circle-dashed'
  | 'droplets'
  | 'zap'
  | 'leaf'
  | 'sun';
// Track 3.13-phase-0: 'shield-alert' + 'shield-check' dropped — used
// only by deleted after_habits_smoking + trust_default slides.

export type InterstitialTrigger =
  | { type: 'concern_includes'; value: PrimaryConcern }
  | { type: 'concern_includes_any'; values: PrimaryConcern[] }
  | { type: 'activity_level'; value: ActivityLevel }
  | { type: 'diet_pattern'; value: DietPattern }
  | { type: 'diagnosed_includes'; value: DiagnosedCondition }
  | { type: 'goal'; value: PrimaryGoal }
  | { type: 'default' };
// Track 3.13-phase-0: 'smoking' + 'alcohol_any' triggers removed — the
// lifestyle step no longer captures smoking/alcohol (moved to clarity-AI
// per quiz-state.ts:21-22), so trigger types were unreachable.

export interface InterstitialSlide {
  id: string;
  slot: InterstitialSlot;
  headline: string;            // ≤ 80 chars
  body: string;                // 2–3 sentences, hedged
  iconName: InterstitialIcon;
  accent: InterstitialAccent;
  triggers: InterstitialTrigger[];
  /** Higher wins when multiple slides match. Default fallbacks use 0. */
  priority: number;
}

// ── Content pool ──────────────────────────────────────────────────────

export const INTERSTITIAL_SLIDES: InterstitialSlide[] = [
  // ── after_concerns ──────────────────────────────────────────────────
  {
    id: 'after_concerns_energy',
    slot: 'after_concerns',
    headline: 'Energy shows up in your bloodwork.',
    body:
      'Low iron, low vitamin D, thyroid signals, and blood sugar patterns are all ' +
      'common factors that rarely get checked by a standard physical.',
    iconName: 'battery-charging',
    accent: 'amber',
    triggers: [{ type: 'concern_includes', value: 'energy' }],
    priority: 10,
  },
  {
    id: 'after_concerns_sleep',
    slot: 'after_concerns',
    headline: 'Sleep quality is often linked to things you can measure.',
    body:
      'Magnesium status, cortisol patterns, and thyroid function are a few signals ' +
      'worth tracking when sleep feels off.',
    iconName: 'moon',
    accent: 'navy',
    triggers: [{ type: 'concern_includes', value: 'sleep' }],
    priority: 10,
  },
  {
    id: 'after_concerns_hormones',
    slot: 'after_concerns',
    headline: 'Hormone signals touch more than you might think.',
    body:
      'Energy, mood, body composition, and recovery often trace back to hormone ' +
      'patterns. Baseline testosterone, SHBG, and thyroid markers are common ' +
      'starting points.',
    iconName: 'sparkles',
    accent: 'amber',
    triggers: [{ type: 'concern_includes', value: 'hormones' }],
    priority: 10,
  },
  {
    id: 'after_concerns_heart',
    slot: 'after_concerns',
    headline: 'Standard cholesterol panels often miss half the picture.',
    body:
      'Markers like ApoB, Lp(a), and hs-CRP are rarely run in routine screenings ' +
      'but may reveal more than total cholesterol alone.',
    iconName: 'heart-pulse',
    accent: 'amber',
    triggers: [{ type: 'concern_includes', value: 'heart' }],
    priority: 11,
  },
  {
    id: 'after_concerns_longevity',
    slot: 'after_concerns',
    headline: 'Biological age is an estimate of how your body is trending.',
    body:
      'Built from inflammation, immune balance, and metabolic signals, it ships ' +
      'with every panel. Lifestyle changes can move it in either direction.',
    iconName: 'clock',
    accent: 'emerald',
    triggers: [{ type: 'concern_includes', value: 'longevity' }],
    priority: 12,
  },
  {
    id: 'after_concerns_weight',
    slot: 'after_concerns',
    headline: 'Weight changes often signal something measurable.',
    body:
      'Thyroid function, insulin signaling, cortisol, and inflammation are common ' +
      'starting points for understanding what your body is doing.',
    iconName: 'activity',
    accent: 'navy',
    triggers: [{ type: 'concern_includes', value: 'weight' }],
    priority: 9,
  },
  {
    id: 'after_concerns_digestion',
    slot: 'after_concerns',
    headline: 'Digestion can reflect what is going on elsewhere.',
    body:
      'Celiac markers, liver function, and inflammation signals are common starting ' +
      'points when digestion feels off.',
    // 'circle-dashed' (the brand's Digestion glyph on Lab Results) read
    // as a placeholder on this larger interstitial surface — swapped to
    // a filled glyph. CONCERN_META still uses CircleDashed on the pill-
    // sized Lab Results surface where it reads fine.
    iconName: 'leaf',
    accent: 'emerald',
    triggers: [{ type: 'concern_includes', value: 'digestion' }],
    priority: 9,
  },
  {
    id: 'after_concerns_mood',
    slot: 'after_concerns',
    headline: 'Mood patterns have measurable signals.',
    body:
      'Thyroid function, vitamin D, B12, and inflammation can all play a role in ' +
      'how you feel day to day.',
    iconName: 'heart',
    accent: 'amber',
    triggers: [{ type: 'concern_includes', value: 'mood' }],
    priority: 9,
  },
  {
    id: 'after_concerns_default',
    slot: 'after_concerns',
    headline: 'Bloodwork can reveal what symptoms cannot explain.',
    body:
      'A comprehensive panel covers energy, metabolism, hormones, and inflammation ' +
      'signals in a single draw.',
    iconName: 'droplets',
    accent: 'navy',
    triggers: [{ type: 'default' }],
    priority: 0,
  },

  // ── after_habits ────────────────────────────────────────────────────
  {
    id: 'after_habits_sedentary',
    slot: 'after_habits',
    headline: 'Activity levels touch a lot of markers over time.',
    body:
      'Chronic inactivity can shift inflammation, blood sugar, and hormone balance. ' +
      'Baseline testing shows where to start.',
    iconName: 'activity',
    accent: 'amber',
    triggers: [{ type: 'activity_level', value: 'sedentary' }],
    priority: 10,
  },
  {
    id: 'after_habits_athletic',
    slot: 'after_habits',
    headline: 'Athletes benefit from baseline markers too.',
    body:
      'Training load affects iron, inflammation, hormone balance, and recovery — ' +
      'baseline testing helps track what is optimal for you.',
    iconName: 'zap',
    accent: 'emerald',
    triggers: [{ type: 'activity_level', value: 'athletic' }],
    priority: 10,
  },
  // Track 3.13-phase-0: after_habits_smoking + after_habits_alcohol
  // slides removed — the lifestyle question no longer captures
  // smoking/alcohol signals (per quiz-state.ts:21-22), so neither
  // slide was reachable in the flow.
  {
    id: 'after_habits_diet',
    slot: 'after_habits',
    headline: 'Restricted diets often shift nutrient status.',
    body:
      'Vitamin B12, iron, and specific minerals are worth tracking when food groups ' +
      'are limited.',
    iconName: 'sun',
    accent: 'navy',
    triggers: [
      { type: 'diet_pattern', value: 'keto' },
      { type: 'diet_pattern', value: 'vegan' },
    ],
    priority: 9,
  },
  {
    id: 'after_habits_default',
    slot: 'after_habits',
    headline: 'Small daily choices compound in your bloodwork.',
    body:
      'Diet, activity, sleep, and stress all leave measurable signals. Testing ' +
      'shows the current state.',
    iconName: 'activity',
    accent: 'navy',
    triggers: [{ type: 'default' }],
    priority: 0,
  },

  // Track 3.13-phase-0: trust_default slide removed alongside its slot
  // type ('trust' was dropped from buildFlow in Phase 1 — see
  // quiz-state.ts:21-22). The "40+ biomarkers per panel" + CLIA-
  // partner + physician-reviewed + HIPAA + HSA/FSA claims preserved
  // here are durable and should carry forward into the Phase 2 value
  // slot copy when that slide is built.

  // ── processing (always shown after the goal step) ───────────────────
  {
    id: 'processing_default',
    slot: 'processing',
    headline: 'Building your recommendation…',
    body:
      'Based on what you shared, we are matching you with the right panel tier. ' +
      'This takes a moment.',
    iconName: 'sparkles',
    accent: 'emerald',
    triggers: [{ type: 'default' }],
    priority: 0,
  },
];

// ── Selector ──────────────────────────────────────────────────────────

function triggerMatches(t: InterstitialTrigger, p: HealthProfileInput): boolean {
  switch (t.type) {
    case 'concern_includes':
      return Boolean(p.primaryConcerns?.includes(t.value));
    case 'concern_includes_any':
      return Boolean(p.primaryConcerns?.some((c) => t.values.includes(c)));
    case 'activity_level':
      return p.activityLevel === t.value;
    case 'diet_pattern':
      return p.dietPattern === t.value;
    case 'diagnosed_includes':
      return Boolean(p.diagnosedConditions?.includes(t.value));
    case 'goal':
      return Boolean(p.primaryGoals?.includes(t.value));
    case 'default':
      return true;
  }
}

/**
 * Pick the highest-priority slide for the given slot whose triggers
 * match the profile. The 'default' fallback in each slot guarantees
 * a result; callers never receive null.
 *
 * Tie-breaker among same-priority matches: slide insertion order in
 * `INTERSTITIAL_SLIDES`. Stable, deterministic.
 */
export function selectInterstitial(
  slot: InterstitialSlot,
  profile: HealthProfileInput,
): InterstitialSlide {
  const candidates = INTERSTITIAL_SLIDES.filter((s) => s.slot === slot);
  // Highest-priority match wins; ties broken by source order.
  let best: InterstitialSlide | null = null;
  for (const slide of candidates) {
    if (!slide.triggers.some((t) => triggerMatches(t, profile))) continue;
    if (best === null || slide.priority > best.priority) best = slide;
  }
  // The default fallback (priority 0, trigger:default) ensures `best`
  // is non-null for every defined slot. Tests guard this invariant.
  if (!best) {
    throw new Error(`selectInterstitial: no fallback for slot "${slot}"`);
  }
  return best;
}
